import axios from 'axios';
import { REST_HOSTNAME } from '../Config';
import { IUserActivitySummary } from './UserActivityApi';
import { IUser } from './UserApi';
import { UserDialogPromptUserSummary } from './UserDialogPromptApi';

const axiosInstance = axios.create({
    baseURL: REST_HOSTNAME
});

// Add request interceptor
axiosInstance.interceptors.request.use(
    function (config) {
        const jwt = localStorage.getItem('jwt');
        if (jwt) {
            config.headers['x-access-token'] = jwt; // Add JWT to the headers
        }
        return config;
    },
    function (error) {
        return Promise.reject(error);
    }
);

export interface ICourseOffering {
    id: number;
    course_id: number | null;
    name: string;
    description: string | null;
    start_date: Date | null;
    end_date: Date | null;
    display_conversation_menu: boolean | null;
    display_office_hour_menu: boolean | null;
}

export type ExerciseActivitySummary = {
    course_id: number;
    lesson_id: number;
    dialog_id: number;
    dialog_prompt_id: number;
    user_count: number;
    incorrect_answer_count: number;
    correct_answer_count: number;
    speaking_attempt_count: number;
    mean_best_speaking_score: number;
};

export type StudentActivitySummary = {
    course_id: number;
    lesson_id: number;
    dialog_id: number;
    dialog_prompt_id: number;
    user_id : number;
    incorrect_answer_count: number;
    correct_answer_count: number;
    speaking_attempt_count: number;
    mean_best_speaking_score: number;
};

type StudentActivityResponse = [IUser[], UserDialogPromptUserSummary[][], IUserActivitySummary[], StudentActivitySummary[]];


export const CourseOfferingApi = {
    getAll: function () {
        return axiosInstance.request<ICourseOffering[]>({
            method: "GET",
            url: `/course_offering`,
            transformResponse: [(data) => {
                const raw = typeof data === 'string' ? JSON.parse(data) : data;
                return (raw as any[]).map(item => ({
                    ...item,
                    start_date: item.start_date == null ? item.start_date : new Date(item.start_date),
                    end_date: item.end_date == null ? item.end_date : new Date(item.end_date),
                }));
            }],
        });
    },
    getById: function (courseOfferingId: number) {
        return axiosInstance.request<ICourseOffering>({
            method: "GET",
            url: `/course_offering/${courseOfferingId}`,
            transformResponse: [(data) => {
                const item = typeof data === 'string' ? JSON.parse(data) : data;
                return {
                    ...item,
                    start_date: item.start_date == null ? item.start_date : new Date(item.start_date),
                    end_date: item.end_date == null ? item.end_date : new Date(item.end_date),
                };
            }],
        });
    },
    create: function (courseOffering: ICourseOffering) {
        return axiosInstance.request<ICourseOffering>({
            method: "POST",
            url: `/course_offering`,
            data: courseOffering
        });
    },
    update: function (courseOffering: ICourseOffering) {
        return axiosInstance.request<string>({
            method: "PUT",
            url: `/course_offering`,
            data: courseOffering
        });
    },
    delete: function (courseOfferingId: number) {
        return axiosInstance.request<string>({
            method: "DELETE",
            url: `/course_offering/${courseOfferingId}`,
        });
    },
    exerciseActivity: function (courseOfferingId: number) {
        return axiosInstance.request<ExerciseActivitySummary[]>({
            method: "GET",
            url: `/course_offering/${courseOfferingId}/exercise_activity`,
        });
    },
    studentActivity: function (courseOfferingId: number) {
        return axiosInstance.request<StudentActivityResponse>({
            method: "GET",
            url: `/course_offering/${courseOfferingId}/student_activity`,
        });
    },
    studentRoster: function (courseOfferingId: number) {
        return axiosInstance.request<IUser[]>({
            method: "GET",
            url: `/course_offering/${courseOfferingId}/student`,
        });
    },
    enroll: function (courseOfferingId: number, email: string) {
        return axiosInstance.request<string>({
            method: "PUT",
            url: `/course_offering/${courseOfferingId}/student/${encodeURIComponent(email)}`,
        });
    },
    drop: function (courseOfferingId: number, email: string) {
        return axiosInstance.request<string>({
            method: "DELETE",
            url: `/course_offering/${courseOfferingId}/student/${encodeURIComponent(email)}`,
        });
    },
    getSignupCode: function (courseOfferingId: number) {
        return axiosInstance.request<string>({
            method: "GET",
            url: `/course_offering/${courseOfferingId}/signup_code`,
        });
    },
    getCurrent: function () {
        return axiosInstance.request<ICourseOffering | null>({
            method: "GET",
            url: `/course_offering/current`,
        });
    },
}
